/**
 * Student Progress Dashboard
 * 
 * Main component for displaying student progress across all subjects.
 * Provides overview and detailed hierarchical views.
 */

import React, { useMemo, useState } from 'react';
import { 
  BookOpen, Clock, TrendingUp, TrendingDown, Target, 
  ChevronRight, ChevronDown, AlertCircle, CheckCircle2,
  Calendar, ArrowLeft, Sparkles
} from 'lucide-react';
import { useProgressOverview, useSubjectProgress } from '../../hooks/useProgress';
import { 
  ScheduleStatus, STATUS_STYLES, formatPercent, formatHours, getStatusLabel,
  SubjectProgressSummary, SubjectProgressDetail, TopicProgress, ObjectiveProgress,
  MASTERY_STYLES
} from '../../types/progress';
import { useThemeStore } from '../../stores/themeStore';
import { getTimeSinceActivity, getProgressBarColor } from '../../services/progressService';
import { buildSubjectDisplayMap } from '../../utils/subjectDisplay';

// ============= SUB-COMPONENTS =============

interface StatusBadgeProps {
  status: ScheduleStatus;
  size?: 'sm' | 'md';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const style = STATUS_STYLES[status];
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${style.bgColor} ${style.textColor} ${sizeClasses}`}>
      <span>{style.icon}</span>
      <span>{getStatusLabel(status)}</span>
    </span>
  );
};

interface ProgressBarProps {
  percent: number;
  height?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ percent, height = 'md', showLabel = false }) => {
  const heightClasses = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  };
  
  const color = getProgressBarColor(percent);
  
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 bg-slate-700/50 rounded-full overflow-hidden ${heightClasses[height]}`}>
        <div 
          className={`${color} h-full rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-sm font-medium text-slate-300 min-w-[3rem] text-right">
          {formatPercent(percent)}
        </span>
      )}
    </div>
  );
};

interface SubjectCardProps {
  subject: SubjectProgressSummary;
  displayName: string;
  onSelect: () => void;
  isDark: boolean;
}

const SubjectCard: React.FC<SubjectCardProps> = ({ subject, displayName, onSelect, isDark }) => {
  const statusStyle = STATUS_STYLES[subject.status];
  
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-5 rounded-2xl border transition-all duration-200 group ${
        isDark 
          ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20' 
          : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${statusStyle.bgColor}`}>
            <BookOpen className={`w-5 h-5 ${statusStyle.textColor}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {displayName}
            </h3>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${
          isDark ? 'text-slate-500' : 'text-slate-400'
        }`} />
      </div>
      
      <ProgressBar percent={subject.completion_percent} showLabel />
      
      <div className="flex items-center justify-between mt-3">
        <StatusBadge status={subject.status} size="sm" />
        <div className={`flex items-center gap-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <Clock className="w-3.5 h-3.5" />
          <span>{formatHours(subject.estimated_hours_remaining)} left</span>
        </div>
      </div>
      
      <div className={`flex items-center gap-4 mt-3 pt-3 border-t ${
        isDark ? 'border-white/10' : 'border-slate-100'
      }`}>
        <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <span className="font-medium">{subject.topics_completed}</span>/{subject.total_topics} topics
        </div>
        <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <span className="font-medium">{subject.objectives_completed}</span>/{subject.total_objectives} objectives
        </div>
        {subject.last_activity_at && (
          <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {getTimeSinceActivity(subject.last_activity_at)}
          </div>
        )}
      </div>
    </button>
  );
};

interface TopicAccordionProps {
  topic: TopicProgress;
  isDark: boolean;
}

const TopicAccordion: React.FC<TopicAccordionProps> = ({ topic, isDark }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const statusStyle = STATUS_STYLES[topic.status];
  
  return (
    <div className={`rounded-xl border overflow-hidden ${
      isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'
    }`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full p-4 text-left flex items-center gap-3 transition-colors ${
          isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
        }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${statusStyle.bgColor}`}>
          {topic.completion_percent >= 100 ? (
            <CheckCircle2 className={`w-4 h-4 ${statusStyle.textColor}`} />
          ) : (
            <Target className={`w-4 h-4 ${statusStyle.textColor}`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {topic.name}
            </h4>
            {topic.can_take_assessment && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                Assessment Available
              </span>
            )}
          </div>
          <div className="mt-1">
            <ProgressBar percent={topic.completion_percent} height="sm" />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {formatPercent(topic.completion_percent)}
          </span>
          {isExpanded ? (
            <ChevronDown className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          ) : (
            <ChevronRight className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className={`px-4 pb-4 pt-2 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
          {/* Subtopics */}
          {topic.subtopics.map((subtopic) => (
            <div key={subtopic.id} className="mb-4 last:mb-0">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  {subtopic.name}
                </span>
                <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  ({subtopic.objectives_completed}/{subtopic.total_objectives})
                </span>
              </div>
              <div className="space-y-2 pl-4 border-l-2 border-slate-700/30">
                {subtopic.objectives.map((obj) => (
                  <ObjectiveItem key={obj.id} objective={obj} isDark={isDark} />
                ))}
              </div>
            </div>
          ))}
          
          {/* Direct objectives (without subtopic) */}
          {topic.direct_objectives.length > 0 && (
            <div className="space-y-2">
              {topic.direct_objectives.map((obj) => (
                <ObjectiveItem key={obj.id} objective={obj} isDark={isDark} />
              ))}
            </div>
          )}
          
          {/* Topic info */}
          <div className={`mt-4 pt-3 border-t flex items-center justify-between text-sm ${
            isDark ? 'border-white/10 text-slate-400' : 'border-slate-100 text-slate-500'
          }`}>
            <span>
              {topic.objectives_completed}/{topic.total_objectives} objectives mastered
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {formatHours(topic.estimated_hours_remaining)} remaining
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

interface ObjectiveItemProps {
  objective: ObjectiveProgress;
  isDark: boolean;
}

const ObjectiveItem: React.FC<ObjectiveItemProps> = ({ objective, isDark }) => {
  const masteryStyle = MASTERY_STYLES[objective.mastery_level];
  const isCompleted = objective.status === 'COMPLETED';
  
  return (
    <div className={`flex items-start gap-2 py-1.5 ${
      isDark ? 'text-slate-300' : 'text-slate-600'
    }`}>
      <div className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
        isCompleted ? 'bg-emerald-500/20' : 'bg-slate-500/20'
      }`}>
        {isCompleted ? (
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-slate-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-sm ${isCompleted ? 'line-through opacity-60' : ''}`}>
          {objective.objective_text}
        </span>
      </div>
      <span className={`text-xs font-medium ${masteryStyle.color}`}>
        {masteryStyle.label}
      </span>
    </div>
  );
};

// ============= MAIN COMPONENT =============

interface StudentProgressDashboardProps {
  onClose?: () => void;
}

export const StudentProgressDashboard: React.FC<StudentProgressDashboardProps> = ({ onClose }) => {
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';
  
  const { data: overview, isLoading: isLoadingOverview, error: overviewError } = useProgressOverview();
  const { data: subjectDetail, isLoading: isLoadingSubject } = useSubjectProgress(selectedSubjectId);
  const subjectDisplayMap = useMemo(() => {
    if (!overview) return {} as Record<string, string>;
    return buildSubjectDisplayMap(
      overview.subjects.map((subject) => ({
        subject_id: subject.subject_id,
        name: subject.subject_name,
      }))
    );
  }, [overview]);
  
  // Loading state
  if (isLoadingOverview) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading your progress...</p>
      </div>
    );
  }
  
  // Error state
  if (overviewError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Failed to load progress
        </h3>
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
          Please try again later or contact support if the problem persists.
        </p>
      </div>
    );
  }
  
  if (!overview) return null;
  
  // Subject detail view
  if (selectedSubjectId && subjectDetail) {
    return (
      <SubjectDetailView
        subject={subjectDetail}
        subjectDisplayName={subjectDisplayMap[selectedSubjectId] ?? subjectDetail.subject_name}
        onBack={() => setSelectedSubjectId(null)}
        isDark={isDark}
        isLoading={isLoadingSubject}
      />
    );
  }
  
  // Overview view
  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Your Progress
          </h2>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Track your learning journey across all subjects
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isDark 
                ? 'bg-white/10 text-white hover:bg-white/20' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Close
          </button>
        )}
      </div>
      
      {/* Overall Stats Card */}
      <div className={`p-6 rounded-2xl border ${
        isDark 
          ? 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/20' 
          : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200'
      }`}>
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
            isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'
          }`}>
            <Sparkles className={`w-7 h-7 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {formatPercent(overview.overall_completion_percent)}
              </span>
              <StatusBadge status={overview.overall_status} />
            </div>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Overall Progress
            </p>
          </div>
        </div>
        
        <ProgressBar percent={overview.overall_completion_percent} height="lg" />
        
        <p className={`mt-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          {overview.status_message}
        </p>
        
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/50'}`}>
            <div className="flex items-center justify-center gap-1 mb-1">
              {overview.overall_status === ScheduleStatus.BEHIND ? (
                <TrendingDown className="w-4 h-4 text-red-400" />
              ) : (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              )}
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {overview.subjects_on_track + overview.subjects_ahead}
              </span>
            </div>
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              On Track
            </span>
          </div>
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/50'}`}>
            <div className="flex items-center justify-center gap-1 mb-1">
              <Clock className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {formatHours(overview.estimated_total_hours_remaining)}
              </span>
            </div>
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Remaining
            </span>
          </div>
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/50'}`}>
            <div className="flex items-center justify-center gap-1 mb-1">
              <BookOpen className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {overview.total_subjects}
              </span>
            </div>
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Subjects
            </span>
          </div>
        </div>
      </div>
      
      {/* Behind Schedule Warning */}
      {overview.subjects_behind > 0 && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${
          isDark 
            ? 'bg-red-500/10 border-red-500/20' 
            : 'bg-red-50 border-red-200'
        }`}>
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>
              You're behind in {overview.subjects_behind} subject{overview.subjects_behind > 1 ? 's' : ''}
            </p>
            <p className={`text-sm mt-1 ${isDark ? 'text-red-400/80' : 'text-red-600'}`}>
              Focus on these subjects to catch up with the schedule.
            </p>
          </div>
        </div>
      )}
      
      {/* Subject Cards */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Your Subjects
        </h3>
        {overview.subjects.length === 0 ? (
          <div className={`text-center py-12 rounded-2xl border ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
          }`}>
            <BookOpen className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              No subjects yet. Subscribe to subjects to start tracking your progress.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {overview.subjects.map((subject) => (
              <SubjectCard
                key={subject.subject_id}
                subject={subject}
                displayName={subjectDisplayMap[subject.subject_id] ?? subject.subject_name}
                onSelect={() => setSelectedSubjectId(subject.subject_id)}
                isDark={isDark}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============= SUBJECT DETAIL VIEW =============

interface SubjectDetailViewProps {
  subject: SubjectProgressDetail;
  subjectDisplayName: string;
  onBack: () => void;
  isDark: boolean;
  isLoading: boolean;
}

const SubjectDetailView: React.FC<SubjectDetailViewProps> = ({ subject, subjectDisplayName, onBack, isDark, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading subject details...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className={`p-2 rounded-xl transition-colors ${
            isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'
          }`}
        >
          <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
        </button>
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {subjectDisplayName}
          </h2>
        </div>
      </div>
      
      {/* Progress Overview */}
      <div className={`p-6 rounded-2xl border ${
        isDark 
          ? 'bg-white/5 border-white/10' 
          : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {formatPercent(subject.completion_percent)}
            </span>
            <StatusBadge status={subject.status} />
          </div>
          <div className={`text-right text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatHours(subject.estimated_hours_remaining)} remaining</span>
            </div>
          </div>
        </div>
        
        <ProgressBar percent={subject.completion_percent} height="lg" />
        
        <p className={`mt-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          {subject.status_message}
        </p>
        
        {/* Schedule info */}
        {subject.schedule && (
          <div className={`mt-4 pt-4 border-t flex items-center gap-4 text-sm ${
            isDark ? 'border-white/10' : 'border-slate-100'
          }`}>
            <div className={`flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Calendar className="w-4 h-4" />
              <span>Started: {new Date(subject.schedule.planned_start).toLocaleDateString()}</span>
            </div>
            <div className={`flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Target className="w-4 h-4" />
              <span>Target: {new Date(subject.schedule.planned_end).toLocaleDateString()}</span>
            </div>
            <div className={`flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <span>{subject.schedule.days_remaining} days left</span>
            </div>
          </div>
        )}
        
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {subject.topics_completed}/{subject.total_topics}
            </span>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Topics</p>
          </div>
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {subject.objectives_completed}/{subject.total_objectives}
            </span>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Objectives</p>
          </div>
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {subject.schedule ? formatPercent(subject.schedule.expected_completion_percent) : '-'}
            </span>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Expected</p>
          </div>
        </div>
      </div>
      
      {/* Topics List */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Topics ({subject.topics.length})
        </h3>
        <div className="space-y-3">
          {subject.topics.map((topic) => (
            <TopicAccordion key={topic.id} topic={topic} isDark={isDark} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudentProgressDashboard;

